'use client';

import { IBlogArticleListItem } from '@/api/types/blog';
import Image from '@/components/Image';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import logger from '@/utils/client-logger';
import { EPlatform } from '@/utils/enums/platform-enum';
import { Arrow_down } from '@zeelool/icon';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import React, { useContext, useEffect, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslations } from 'next-intl';
interface ArticleCardProps {
  article: IBlogArticleListItem;
  showIntro?: boolean;
  titleIsH2?: boolean;
  onArticleExp?: (article: IBlogArticleListItem) => void;
  onArticleClick?: (article: IBlogArticleListItem) => void;
  onAuthorClick?: (article: IBlogArticleListItem) => void;
  className?: string;
  variant?: 'default' | 'banner-main' | 'banner-slide' | 'top-read-article';
}

const ArticleCard: React.FC<ArticleCardProps> = ({
  article,
  showIntro = false,
  titleIsH2 = false,
  onArticleExp = () => {},
  onArticleClick = () => {},
  onAuthorClick = () => {},
  className = '',
  variant = 'default',
}) => {
  const t = useTranslations('blog.components');
  const router = useRouter();
  const { deviceType } = useContext(ResponsiveContext);
  const waiMode = useStore((state) => state.waiMode);
  const inAppH5 = [EPlatform.IOS, EPlatform.ANDROID].includes(deviceType);
  // 使用useInView处理曝光事件
  const [articleRef, inView] = useInView({
    threshold: 0.5,
    triggerOnce: true,
  });

  // 计算文章图片URL
  const articleImage = useMemo(() => {
    try {
      const { image = '', image_url = '' } = article;
      if (image.includes('http')) {
        return image;
      }
      if (image_url.includes('http')) {
        return image_url;
      }
      return image;
    } catch (error) {
      logger.error(error);
      return '';
    }
  }, [article]);

  // 处理曝光事件
  useEffect(() => {
    if (inView) {
      onArticleExp(article);
    }
  }, [inView]);

  // 跳转到文章详情
  const toArticle = (e: React.MouseEvent) => {
    e.preventDefault();
    onArticleClick(article);
    if (inAppH5) {
      router.push(`/blog/${article.another_name || 'all-blogs'}/${article.jump_path}`);
    } else {
      window.open(`/blog/${article.another_name || 'all-blogs'}/${article.jump_path}`, '_blank');
    }
  };

  // 跳转到作者页面
  const toAuthor = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    onAuthorClick(article);
    if (inAppH5) {
      router.push(`/blog/authors/${article.author_path}`);
    } else {
      window.open(`/blog/authors/${article.author_path}`, '_blank');
    }
  };

  // 动态标题组件
  const TitleComponent = titleIsH2 ? 'h2' : 'h3';

  // 根据 variant 计算样式类名
  const getImageClasses = () => {
    const baseClasses = 'w-full block';
    switch (variant) {
      case 'banner-main':
        return `${baseClasses} h-auto aspect-auto`;
      default:
        return `${baseClasses}`;
    }
  };

  const getTitleClasses = () => {
    const baseClasses = 'mt-16';
    switch (variant) {
      case 'banner-main':
        return `${baseClasses} px-14`;
      default:
        return baseClasses;
    }
  };

  const getTitleTextClasses = () => {
    const wapBaseClasses =
      'title text-16 font-bb leading-24 max-h-48 overflow-hidden text-ellipsis line-clamp-2 cursor-pointer';

    const webBaseClasses =
      'title text-24 font-bb mt-24 leading-30 max-h-60 overflow-hidden text-ellipsis line-clamp-2 cursor-pointer';
    const baseClasses = deviceType === EPlatform.WEB ? webBaseClasses : wapBaseClasses;
    switch (variant) {
      case 'banner-main':
        return `${baseClasses} mt-20`;
      case 'top-read-article':
        return `${baseClasses} text-20`;
      default:
        return baseClasses;
    }
  };

  const getBottomClasses = () => {
    const wapBaseClasses = 'article-bottom flex justify-between items-center mt-20';
    const webBaseClasses = 'article-bottom flex justify-between items-center mt-24';
    const baseClasses = deviceType === EPlatform.WEB ? webBaseClasses : wapBaseClasses;
    switch (variant) {
      case 'banner-main':
        return `${baseClasses} px-14`;
      default:
        return baseClasses;
    }
  };

  const getImageContainerStyle = () => {
    switch (variant) {
      case 'banner-main':
        return {}; // 不设置固定宽高比
      default:
        return { aspectRatio: '16 / 9' };
    }
  };

  return (
    <div ref={articleRef} className={`article-item block mb-[21.333vw] md:mb-0 ${className}`}>
      {/* 文章图片 */}
      <Link
        prefetch={false}
        href={`/blog/${article.another_name || 'all-blogs'}/${article.jump_path}`}
        className={`article-item-img ${getImageClasses()}`}
        style={getImageContainerStyle()}
        aria-label={article.name}
        onClick={toArticle}
      >
        {articleImage && (
          <Image
            src={articleImage}
            alt={article.name || 'Article Image'}
            width={400}
            height={225}
            className="w-full h-full object-cover block"
          />
        )}
      </Link>

      {/* 文章标题和介绍 */}
      <div className={`article-item-title ${getTitleClasses()}`} onClick={toArticle}>
        <TitleComponent className={getTitleTextClasses()}>{article.name}</TitleComponent>
        {showIntro && article.intro && (
          <div
            className={`
              article-item-intro mt-12 font-ir overflow-hidden text-ellipsis line-clamp-3
              ${deviceType === EPlatform.WEB ? 'text-14 leading-22 max-h-66' : 'text-13 leading-20 max-h-60'}
            `}
          >
            {article.intro}
          </div>
        )}
      </div>

      {/* 文章底部：作者信息 + Read More */}
      <div className={getBottomClasses()}>
        {/* 作者信息 */}
        <Link
          prefetch={false}
          href={`/blog/authors/${article.author_path}`}
          onClick={toAuthor}
          className="article-bottom-author flex items-center"
          aria-label={article.name}
        >
          <div
            className={`article-bottom-author-img rounded-full overflow-hidden ${
              deviceType === EPlatform.WEB ? 'w-36 h-36' : 'w-34 h-34'
            }`}
          >
            {article.head_portrait && (
              <Image
                src={article.head_portrait}
                alt={article.blog_author_name || 'Author'}
                width={34}
                height={34}
                className="w-full h-full object-cover"
              />
            )}
          </div>
          <div
            className={`article-bottom-author-info  ${
              deviceType === EPlatform.WEB ? 'ml-12' : 'ml-8'
            }`}
          >
            <div
              className={`author-name text-14 font-bb truncate ${
                deviceType === EPlatform.WEB ? 'max-w-[200px]' : 'max-w-[220px]'
              }`}
            >
              {article.blog_author_name}
            </div>
            <div
              className={`date text-12 ${waiMode ? 'text-primary-black' : 'text-[#808080]'} ${deviceType === EPlatform.WEB ? 'mt-6' : 'mt-4'}`}
            >
              {article.up_at}
            </div>
          </div>
        </Link>

        {/* Read More 按钮 */}
        <div
          className={`read-more text-12 border-none w-fit h-fit p-0 text-[#282828] font-normal flex items-center cursor-pointer ${
            deviceType === EPlatform.WEB ? 'text-14' : 'text-12'
          }`}
          onClick={toArticle}
        >
          <div className="underline font-bs">{t('readMore')}</div>
          <Arrow_down
            className={`ml-8 rotate-270 ${deviceType === EPlatform.WEB ? 'text-16s' : 'text-12'}`}
          />
        </div>
      </div>
    </div>
  );
};

export default ArticleCard;
